import { useMemo } from 'react';
import { useUserRole } from './useQueries';
import { useRoleDefinitions } from './useQueries';

/**
 * Custom hook for checking if the current user has a specific role or higher privilege
 * Uses server-side role definitions and hierarchy for security
 * 
 * @param {string} requiredRole - The role required to access a feature (viewer, user, endorser, admin)
 * @returns {Object} - Object containing hasRole boolean, isLoading state, and error
 * 
 * @example
 * // Check if user has admin privileges
 * const { hasRole, isLoading } = useHasRole('admin');
 * 
 * @example
 * // Check if user has endorser or higher privileges
 * const { hasRole, isLoading } = useHasRole('endorser');
 * if (hasRole && !isLoading) {
 *   // Show endorser-only features
 * }
 */
export function useHasRole(requiredRole) {
    const { roles, isLoading: rolesLoading, error: rolesError } = useUserRole();
    const { data: roleDefinitions, isLoading: definitionsLoading, error: definitionsError } = useRoleDefinitions();
    
    const hasRole = useMemo(() => {
        // Return false while loading or if there's an error
        if (rolesLoading || definitionsLoading || rolesError || definitionsError || !roles || roles.length === 0 || !roleDefinitions?.hierarchy) {
            return false;
        }
        
        // Use server-provided role hierarchy
        const hierarchy = roleDefinitions.hierarchy;
        
        // Return false if required role is invalid
        const requiredLevel = hierarchy[requiredRole];
        if (!requiredLevel) {
            return false;
        }
        
        // Find the user's maximum role level using server hierarchy
        const userMaxLevel = Math.max(
            ...roles.map(role => hierarchy[role] || 0)
        );
        
        // User has sufficient privilege if their max level >= required level
        return userMaxLevel >= requiredLevel;
    }, [roles, requiredRole, rolesLoading, definitionsLoading, rolesError, definitionsError, roleDefinitions]);
    
    return {
        hasRole,
        isLoading: rolesLoading || definitionsLoading,
        error: rolesError || definitionsError,
        roleDefinitions
    };
}